import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { RefreshCw } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import type { User } from '@/lib/types'

interface ChangePasswordDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  user: User | null
  password: string
  onPasswordChange: (password: string) => void
  onSubmit: () => void
  isLoading: boolean
  getRoleBadgeColor: (role: string) => string
}

export function ChangePasswordDialog({
  open,
  onOpenChange,
  user,
  password,
  onPasswordChange,
  onSubmit,
  isLoading,
  getRoleBadgeColor,
}: ChangePasswordDialogProps) {
  const { t } = useTranslation()

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('admin.users.changePassword')}</DialogTitle>
          <DialogDescription>{t('admin.users.changePasswordDescription')}</DialogDescription>
        </DialogHeader>
        {user && (
          <div className="space-y-4">
            <div className="p-4 border rounded-lg bg-muted/50">
              <div className="flex items-center space-x-3">
                <div className="flex-1">
                  <p className="font-medium">
                    {user.displayName || `${user.firstName} ${user.lastName}`}
                  </p>
                  <p className="text-sm text-muted-foreground">{user.email}</p>
                </div>
                {user.roles && user.roles.length > 0 ? (
                  <Badge className={getRoleBadgeColor(user.roles[0].slug)}>
                    {user.roles[0].name}
                  </Badge>
                ) : null}
              </div>
            </div>
            <div>
              <Label htmlFor="new_password">{t('admin.users.newPassword')}</Label>
              <Input
                id="new_password"
                type="password"
                value={password}
                onChange={e => onPasswordChange(e.target.value)}
                placeholder={t('admin.users.newPasswordPlaceholder')}
              />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
            {t('admin.users.cancel')}
          </Button>
          <Button variant="gradient" onClick={onSubmit} disabled={isLoading || !password}>
            {isLoading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : null}
            {t('admin.users.changePassword')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
